import React, { useState } from 'react';
import { MapPin, Globe, Phone, Mail, CheckCircle2 } from 'lucide-react';
import { contactSubjects } from '../data/mock';
import ErrorModal from '../components/ErrorModal';
import { useLanguage } from '../context/LanguageContext';
import SEO, { breadcrumbSchema } from '../components/SEO';

const Contact = () => {
  const { t, tx, language } = useLanguage();
  const fr = language === 'fr';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    country: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Validate field in real-time and clear error only if valid
    if (name === 'name' && value.trim()) {
      setInvalidFields(prev => prev.filter(f => f !== 'name'));
    } else if (name === 'email') {
      if (value.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setInvalidFields(prev => prev.filter(f => f !== 'email'));
      }
    } else if (name === 'subject' && value) {
      setInvalidFields(prev => prev.filter(f => f !== 'subject'));
    } else if (name === 'message' && value.trim()) {
      setInvalidFields(prev => prev.filter(f => f !== 'message'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields with specific error messages
    const validationErrors = [];
    const invalid = [];
    
    if (!formData.name.trim()) {
      validationErrors.push(t('validation.nameEmpty'));
      invalid.push('name');
    }
    
    if (!formData.email.trim()) {
      validationErrors.push(t('validation.emailRequired'));
      invalid.push('email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      validationErrors.push(t('validation.emailInvalidDetail'));
      invalid.push('email');
    }
    
    if (!formData.subject || formData.subject === '') {
      validationErrors.push(t('validation.subjectRequired'));
      invalid.push('subject');
    }
    
    if (!formData.message.trim()) {
      validationErrors.push(t('validation.messageEmpty'));
      invalid.push('message');
    } else if (formData.message.trim().length < 10) {
      validationErrors.push(t('validation.messageMinLength'));
      invalid.push('message');
    }
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setInvalidFields(invalid);
      setShowErrorModal(true);
      // Scroll to first invalid field
      const firstInvalid = document.getElementsByName(invalid[0])[0];
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    try {
      // Step 1: Save to backend FIRST to get order ID
      let orderId = null;
      try {
        const dbRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'contact',
            customer_name: formData.name,
            customer_email: formData.email,
            customer_phone: formData.phone,
            customer_company: formData.company,
            product: '',
            series: '',
            message: formData.message,
            source_page: '/contact',
            details: {
              subject: formData.subject,
              city: formData.city,
              country: formData.country,
            },
          }),
        });
        const dbData = await dbRes.json();
        orderId = dbData.id;
      } catch (dbErr) {
        console.warn('Backend save failed:', dbErr);
      }

      // Step 2: Send to Web3Forms with admin dashboard link
      const adminLink = orderId
        ? `${window.location.origin}/portal/admin?tab=orders&order=${orderId}`
        : '';
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '4deb5ebc-3d5b-446d-9890-9acae8b33da4',
          subject: `Contact Form - ${formData.subject}`,
          ...formData,
          'View in Admin Dashboard': adminLink || 'Link unavailable',
        }),
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
        setFormData({ name: '', email: '', phone: '', company: '', city: '', country: '', subject: '', message: '' });
        setInvalidFields([]);
      } else {
        // Parse API error response
        const apiErrors = [];
        const apiInvalid = [];
        
        if (data.message) {
          // Check for field-specific errors in API response
          if (data.message.includes('email') || data.message.includes('Email')) {
            apiErrors.push(t('validation.emailInvalidApi'));
            apiInvalid.push('email');
          } else if (data.message.includes('spam') || data.message.includes('bot')) {
            apiErrors.push(t('validation.spamDetected'));
          } else {
            apiErrors.push(`${fr ? 'Échec de la soumission' : 'Submission failed'}: ${data.message}`);
          }
        } else {
          apiErrors.push(t('validation.submissionFailed'));
        }
        
        setErrors(apiErrors);
        setInvalidFields(apiInvalid);
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Form submission error', err);
      const networkErrors = [t('validation.networkError')];
      
      // Check if it's a validation error from the network
      if (err.message && err.message.includes('Failed to fetch')) {
        networkErrors.push(t('validation.serverUnreachable'));
      }
      
      setErrors(networkErrors);
      setShowErrorModal(true);
    }
  };

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title="Contact Us"
        description="Get in touch with Total Composites. Request a quote, ask about our expedition truck campers, or visit us in Quebec, Canada. Phone, email, and online form available."
        path="/contact"
        keywords="Total Composites contact, truck camper quote, expedition camper inquiry, camper dealer Canada, order truck camper"
        jsonLd={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Contact', path: '/contact' }
        ])}
      />
      {/* Header */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {t('contact.title')}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {language === 'fr' ? 'Entrez en contact' : 'Get in Touch'}
          </h1>
          <p className="text-gray-400 max-w-xl leading-relaxed">
            {language === 'fr' ? 'Des questions sur nos produits ou intéressé à devenir partenaire ? Nous sommes là pour vous aider.' : "Have questions about our products or interested in becoming a partner? We're here to help."}
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-14">
            {/* Form */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('contact.formTitle')}
              </h2>
              <p className="text-gray-500 text-sm mb-8">{language === 'fr' ? 'Remplissez le formulaire ci-dessous et notre équipe vous répondra rapidement.' : 'Fill out the form below and our team will respond promptly.'}</p>

              {submitted && (
                <div className="mb-6 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-4">
                  <CheckCircle2 size={18} className="text-green-600 flex-shrink-0" />
                  <p className="text-green-800 text-sm font-medium">
                    {language === 'fr' ? 'Merci ! Votre demande a été soumise. Nous vous contacterons sous peu.' : "Thank you! Your inquiry has been submitted. We'll be in touch shortly."}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Honeypot for spam protection - hidden from users */}
                <input type="checkbox" name="botcheck" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                      {language === 'fr' ? 'Nom' : 'Name'} <span style={{ color: '#E8520A' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                      style={{ 
                        borderColor: invalidFields.includes('name') ? '#EF4444' : '#e5e7eb',
                        backgroundColor: invalidFields.includes('name') ? '#FEF2F2' : '#fff'
                      }}
                      onFocus={(e) => e.target.style.borderColor = invalidFields.includes('name') ? '#EF4444' : '#E8520A'}
                      onBlur={(e) => e.target.style.borderColor = invalidFields.includes('name') ? '#EF4444' : '#e5e7eb'}
                      placeholder={language === 'fr' ? 'Votre nom complet' : 'Your full name'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                      {language === 'fr' ? 'Courriel' : 'Email'} <span style={{ color: '#E8520A' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                      style={{ 
                        borderColor: invalidFields.includes('email') ? '#EF4444' : '#e5e7eb',
                        backgroundColor: invalidFields.includes('email') ? '#FEF2F2' : '#fff'
                      }}
                      onFocus={(e) => e.target.style.borderColor = invalidFields.includes('email') ? '#EF4444' : '#E8520A'}
                      onBlur={(e) => e.target.style.borderColor = invalidFields.includes('email') ? '#EF4444' : '#e5e7eb'}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{language === 'fr' ? 'Téléphone' : 'Phone'}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                      onFocus={(e) => e.target.style.borderColor = '#E8520A'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{language === 'fr' ? 'Entreprise' : 'Company'}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                      onFocus={(e) => e.target.style.borderColor = '#E8520A'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      placeholder={language === 'fr' ? 'Nom de l’entreprise' : 'Company name'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{language === 'fr' ? 'Ville' : 'City'}</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                      onFocus={(e) => e.target.style.borderColor = '#E8520A'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      placeholder={language === 'fr' ? 'Votre ville' : 'Your city'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{language === 'fr' ? 'Pays' : 'Country'}</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                      onFocus={(e) => e.target.style.borderColor = '#E8520A'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      placeholder={language === 'fr' ? 'Votre pays' : 'Your country'}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{language === 'fr' ? 'Sujet' : 'Subject'}</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg text-sm text-gray-900 focus:outline-none transition-all bg-white"
                    style={{ 
                      borderColor: invalidFields.includes('subject') ? '#EF4444' : '#e5e7eb',
                      backgroundColor: invalidFields.includes('subject') ? '#FEF2F2' : '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = invalidFields.includes('subject') ? '#EF4444' : '#E8520A'}
                    onBlur={(e) => e.target.style.borderColor = invalidFields.includes('subject') ? '#EF4444' : '#e5e7eb'}
                  >
                    <option value="">{language === 'fr' ? 'Sélectionner un sujet' : 'Select a subject'}</option>
                    {contactSubjects.map((s) => (
                      <option key={tx(s)} value={tx(s)}>{tx(s)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                    {language === 'fr' ? 'Message' : 'Message'} <span style={{ color: '#E8520A' }}>*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all resize-none"
                    style={{ 
                      borderColor: invalidFields.includes('message') ? '#EF4444' : '#e5e7eb',
                      backgroundColor: invalidFields.includes('message') ? '#FEF2F2' : '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = invalidFields.includes('message') ? '#EF4444' : '#E8520A'}
                    onBlur={(e) => e.target.style.borderColor = invalidFields.includes('message') ? '#EF4444' : '#e5e7eb'}
                    placeholder={language === 'fr' ? 'Parlez-nous de votre projet ou de votre demande...' : 'Tell us about your project or inquiry...'}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
                  style={{ backgroundColor: '#E8520A' }}
                >
                  {language === 'fr' ? 'Soumettre la demande' : 'Submit Inquiry'}
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-2xl p-7">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-5">{language === 'fr' ? 'Coordonnées' : 'Contact Information'}</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
                      <MapPin size={16} style={{ color: '#E8520A' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-0.5">{language === 'fr' ? 'Siège social' : 'Headquarters'}</p>
                      <a
                        href="https://maps.app.goo.gl/R83NYMrWMjo5BXMi7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-gray-800 font-medium hover:text-orange-600 transition-colors"
                      >
                        Victoria, BC, Canada
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
                      <Phone size={16} style={{ color: '#E8520A' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-0.5">{language === 'fr' ? 'Téléphone' : 'Phone'}</p>
                      <a href="tel:2504770375" className="text-sm text-gray-800 font-medium hover:text-orange-600 transition-colors">
                        250 477 0375
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
                      <Mail size={16} style={{ color: '#E8520A' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-0.5">{language === 'fr' ? 'Courriel' : 'Email'}</p>
                      <a href="mailto:info@totalcomposites.com" className="text-sm text-gray-800 font-medium hover:text-orange-600 transition-colors">
                        info@totalcomposites.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
                      <Globe size={16} style={{ color: '#E8520A' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-0.5">{language === 'fr' ? 'Site web' : 'Website'}</p>
                      <p className="text-sm text-gray-800 font-medium">totalcomposites.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-7">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4">{language === 'fr' ? 'Vous souhaitez acheter ?' : 'Looking to Buy?'}</h3>
                
                <div className="space-y-5">
                  {/* Direct Order */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2">{language === 'fr' ? 'Prêt à démarrer votre projet ?' : 'Ready To Start Your Project?'}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      {language === 'fr'
                        ? 'Nos campeurs portés 2026 et le Kei Truck Box sont disponibles à la commande en ligne en tant que coques vides — la fondation parfaite pour votre aménagement personnalisé.'
                        : 'Our 2026 truck campers and Kei Truck Box are available to order online as empty shells — the perfect foundation for your custom build-out.'}
                    </p>
                    <div className="flex flex-col gap-2">
                      <a
                        href="/products?tab=pre-assembled"
                        className="inline-flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
                        style={{ color: '#E8520A' }}
                      >
                        {language === 'fr' ? 'DÉCOUVREZ NOS CAMPEURS PORTÉS 2026 →' : 'ORDER 2026 CAMPERS →'}
                      </a>
                      <a
                        href="/products?tab=kei-truck"
                        className="inline-flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
                        style={{ color: '#E8520A' }}
                      >
                        {language === 'fr' ? 'DÉCOUVREZ NOS KEI TRUCK BOX →' : 'ORDER KEI TRUCK BOX →'}
                      </a>
                    </div>
                  </div>

                  {/* Partners */}
                  <div className="border-t border-gray-200 pt-5">
                    <h4 className="text-sm font-bold text-gray-900 mb-2">{language === 'fr' ? 'Travailler avec nos partenaires' : 'Work With Our Partners'}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      {language === 'fr'
                        ? 'Nos partenaires formés et de confiance créent des campeurs portés entièrement aménagés adaptés à votre aventure.'
                        : 'Our trained and trusted partners create fully built-out campers tailored to your adventure.'}
                    </p>
                    <a
                      href="/partners"
                      className="inline-flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
                      style={{ color: '#E8520A' }}
                    >
                      {language === 'fr' ? 'TROUVER UN PARTENAIRE →' : 'FIND A PARTNER →'}
                    </a>
                  </div>

                  {/* Custom Projects */}
                  <div className="border-t border-gray-200 pt-5">
                    <h4 className="text-sm font-bold text-gray-900 mb-2">{language === 'fr' ? 'Projets sur mesure' : 'Custom Projects'}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {language === 'fr'
                        ? 'Kits expédition, carrosseries, habitacles d’expédition ou projets sur mesure — remplissez le formulaire ci-dessus et nous vous aiderons à concrétiser votre vision.'
                        : 'Expedition kits, truck bodies, expedition enclosures, or bespoke projects — fill out the form above and we’ll help bring your vision to life.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-2 rounded-2xl p-7" style={{ borderColor: '#E8520A' }}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: '#E8520A' }}>{language === 'fr' ? 'Devenir partenaire' : 'Become a Partner'}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {language === 'fr'
                    ? 'Intéressé à devenir client grossiste ? Nous offrons de la formation, du soutien et une gamme de produits éprouvée pour les constructeurs professionnels.'
                    : 'Interested in becoming a wholesale customer? We provide training, support, and a proven product line for professional builders.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Error Modal */}
      <ErrorModal 
        show={showErrorModal} 
        onClose={() => setShowErrorModal(false)} 
        errors={errors} 
      />
    </div>
  );
};

export default Contact;
